import {
  URLS,
  DATE_FORMAT,
  FNS_DATE_FORMAT,
  TOKEN_KEY,
  IMPERSONATION_TOKEN_KEY,
  IMPERSONATOR_EMAIL_KEY
} from 'utils/constants'
import { isTokenExpired } from './jwtHelper'
import { jwtDecode } from 'jwt-decode'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { removePaymentOverdueDate, removeModules } from './paymentService'

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY)
}

const removeToken = () => {
  return localStorage.removeItem(TOKEN_KEY)
}

export const isAuthenticated = () => {
  // Checks if there is a saved token and it's still valid
  const token = getToken()
  if (token) {
    return !isTokenExpired(token)
  } else {
    return false
  }
}

export const authenticate = token => {
  localStorage.setItem(TOKEN_KEY, token)
}

export const setToken = token => {
  authenticate(token)
}

export const refreshToken = async cb => {
  const token = await makeGetRequest('/v1/users/refresh')
  setToken(token)
  cb?.()
}

export const logout = async () => {
  // ALWAYS check and exit impersonation first to clean up backend state
  // This prevents database inconsistencies and "already impersonating" errors
  if (isImpersonating()) {
    try {
      await makePostRequest('/v1/users/exit-impersonation')
    } catch (error) {
      // Log but continue - we still want to clear local state even if API fails
      console.error('Failed to exit impersonation during logout:', error)
    }
  }

  // Clear all local storage
  removeToken()
  removePaymentOverdueDate()
  removeModules()
  removeImpersonationData()
}

export const getImpersonationToken = () => {
  return localStorage.getItem(IMPERSONATION_TOKEN_KEY)
}

export const getImpersonatorEmail = () => {
  return localStorage.getItem(IMPERSONATOR_EMAIL_KEY)
}

export const isImpersonating = () => {
  return !!getImpersonationToken()
}

/**
 * While impersonating, IMPERSONATION_TOKEN_KEY holds the admin's own token. That token is the only
 * way back: exiting impersonation returns no credentials, so the server can never turn an
 * impersonation token into an admin session.
 */
export const setImpersonationData = (adminToken, impersonatorEmail) => {
  localStorage.setItem(IMPERSONATION_TOKEN_KEY, adminToken)
  localStorage.setItem(IMPERSONATOR_EMAIL_KEY, impersonatorEmail)
}

export const removeImpersonationData = () => {
  localStorage.removeItem(IMPERSONATION_TOKEN_KEY)
  localStorage.removeItem(IMPERSONATOR_EMAIL_KEY)
}

export const getUser = () => {
  const token = getToken()
  try {
    return token && jwtDecode(token)
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const isAdmin = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).auth.some(r => r === 'ROLE_ADMIN')
    }
    return false
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const isTimetrackerRegular = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).auth.some(r => r === 'ROLE_TIMETRACKER')
    }
    return false
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const isTimetrackerAdmin = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).auth.some(r => r === 'ROLE_TIMETRACKER_ADMIN')
    }
    return false
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const isOnlyTimetracker = token => {
  const roles = getUserRoles(token)
  if (roles && roles.length === 1) {
    return ['ROLE_TIMETRACKER_ADMIN', 'ROLE_TIMETRACKER'].includes(roles[0])
  }
  return false
}

export const getUserRoles = token => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(token || getToken()).auth
    }
    return false
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getUserDepartment = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).department
    }
    return ''
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getTenant = token => {
  try {
    if (token) return jwtDecode(token).tenant

    if (isAuthenticated()) {
      return jwtDecode(getToken()).tenant
    }
    return ''
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getTenantUrl = token => {
  try {
    return jwtDecode(token).tenant_url
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getCurrency = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).currency
    }
    return ''
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getTimezone = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).timezone
    }
    return ''
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getDateFormat = (prop = null) => {
  try {
    if (isAuthenticated()) {
      const format = jwtDecode(getToken()).date_format
      return prop && prop.fns ? FNS_DATE_FORMAT[format] : DATE_FORMAT[format]
    }
    return ''
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}

export const getOrganizationInterest = () => {
  try {
    if (isAuthenticated()) {
      return jwtDecode(getToken()).organization_interest
    }
    return ''
  } catch (e) {
    logout()
    window.location.href = URLS.LOGIN
  }
}
